import pMap from 'p-map'
import { FormattedMessage } from 'react-intl'
import { QueryClient, useIsFetching, useQueryClient } from 'react-query'

import { saveCsv } from '@/components/BulkUpload/util/csv'
import Button from '@/components/Button'
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner'

import messages from './messages'

async function exportAllUsers(client: QueryClient) {
  // Get users count
  const response = await search(client, { count: 0, sortBy: 'name.familyName', startIndex: 1 })

  // Create request params chunks
  const paramsChunks = Array.from({ length: Math.ceil(response.totalResults / 1000) }, (_, index) => {
    return { count: 1000, sortBy: 'name.familyName', startIndex: index * 1000 + 1 }
  })

  // Fetch chunks in parallel (limit to 1 as search has been unstable...)
  const responses = await pMap(paramsChunks, params => search(client, params), { concurrency: 1 })

  // Collect results
  const users = responses
    .flatMap(response => response.resources)
    .map(resource => {
      const user = {
        id: resource.id,
        externalId: resource.externalId,
        email: resource.email,
        displayName: resource.displayName,
        active: resource.active,
        createdAt: resource.createdAt,
        updatedAt: resource.lastModified
      }

      for (const schema of resource.schemas) {
        if (schema in resource) Object.assign(user, resource[schema])
      }

      return user
    })

  // Save as CSV
  saveCsv(users, 'users.csv')
}

type SearchParams = {
  count: number
  sortBy: string
  startIndex: number
}

type SearchResponse = {
  data: {
    itemsPerPage: number
    startIndex: number
    totalResults: number
    resources: UserSearchResult
  }
}

type UserSearchResult = {
  schemas: string[]
  id: string
  externalId: string
  email: string
  active: boolean
  userName: string
  displayName: string
  name: { familyName: string; givenName: string }
  preferredLanguage: string
  createdAt: string
  createdBy: string
  lastModified: string
  updatedBy: string
} & Record<string, Record<string, unknown>>

async function search(client: QueryClient, params: SearchParams) {
  const response = await client.fetchQuery<SearchResponse>({
    queryKey: [
      '/v1/admin/organizations/:organizationId/users/search',
      {
        method: 'post',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(params)
      }
    ]
  })
  return response.data
}

export default function ExportAllUsers() {
  const client = useQueryClient()
  const isFetching = useIsFetching(['/v1/admin/organizations/:organizationId/users/search'])

  return (
    <Button
      color="secondary"
      onClick={() => (isFetching ? null : exportAllUsers(client))}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 150,
        height: 41,
        opacity: isFetching ? 0.8 : 1,
        pointerEvents: isFetching ? 'none' : 'initial'
      }}
    >
      {isFetching ? (
        <LoadingSpinner style={{ width: '1em', height: '1em' }} />
      ) : (
        <FormattedMessage {...messages.exportAllUsers} />
      )}
    </Button>
  )
}
